import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, FileDown, Calendar as CalIcon } from "lucide-react";
import api from "../../lib/api";
import { BRAND_COLORS, withAlpha } from "../../lib/bookingStatus";
import { useI18n } from "../../lib/i18n";
import { deDateLong } from "../../lib/dateFormat";

/**
 * Feb 2026 · User-Directive „Klick auf Bericht öffnet Detailansicht" (5
 * Sep 2026). Vollbild-Modal (Portal an body) mit grosser Foto-Galerie,
 * voller Zusammenfassung, persoenlichem Fazit und — falls vorhanden —
 * einer Aktivitaeten-Liste (Auto-Generation aus completed requests /
 * calendar events). Der Detail-Modal ist der Ort, an dem der Bericht
 * offiziell "geoeffnet" wird — dieselbe Komponente stoesst nach dem
 * Mount `mark-read` an (siehe `onOpen` in `CustomerUpdates.jsx`), damit
 * der Bell-Badge sofort dekrementiert.
 */
function BigPhoto({ fileId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    (async () => {
      try {
        const res = await api.get(`/files/${fileId}`, { responseType: "blob" });
        url = URL.createObjectURL(res.data);
        setSrc(url);
      } catch { /* ignore */ }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [fileId]);
  if (!src) return <div className="bg-navy/5 aspect-[4/3] animate-pulse w-full" />;
  return <img src={src} alt="Bericht" className="object-cover w-full aspect-[4/3]" />;
}

export default function WeeklyReportDetailModal({ update, property, onClose }) {
  const { lang } = useI18n();
  const de = lang === "de";
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const photoIds = update?.photo_file_ids?.length
    ? update.photo_file_ids
    : (update?.photo_file_id ? [update.photo_file_id] : []);

  const weekLabel = update?.week_of ? deDateLong(update.week_of) : "";
  const activities = Array.isArray(update?.activities) ? update.activities : [];

  const node = (
    <div
      className="fixed inset-0 z-[60] flex items-start md:items-center justify-center p-0 md:p-6"
      style={{ background: withAlpha(BRAND_COLORS.navyDark, 0.6) }}
      onClick={onClose}
      data-testid="weekly-report-modal"
    >
      <div
        className="relative bg-ivory w-full max-w-[1100px] max-h-full md:max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 flex items-center justify-between gap-4 bg-navy text-ivory px-5 md:px-8 py-4"
          style={{ background: BRAND_COLORS.navy }}
        >
          <div className="min-w-0">
            <div className="label-caps text-ivory/70 mb-1 text-[10px]">
              <CalIcon className="w-3 h-3 inline-block mr-1.5 -mt-0.5" strokeWidth={1.6} />
              {de ? "Woche vom" : "Week of"} {weekLabel}
            </div>
            <div className="font-display text-xl md:text-2xl truncate">
              {property?.name || (de ? "Bericht" : "Report")}
            </div>
          </div>
          <button
            data-testid="weekly-report-modal-close"
            onClick={onClose}
            className="text-ivory/80 hover:text-ivory p-1"
            aria-label={de ? "Schließen" : "Close"}
          >
            <X className="w-5 h-5" strokeWidth={1.6} />
          </button>
        </div>

        <div className="px-5 md:px-8 py-6 md:py-8 grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-10">
          {/* Photos */}
          <div className="lg:col-span-3">
            {photoIds.length === 0 ? (
              <div className="bg-ivory/60 border border-navy/10 aspect-[4/3] flex items-center justify-center label-caps">
                {de ? "Kein Foto" : "No photo"}
              </div>
            ) : (
              <div>
                <div className="relative bg-navy/5">
                  <BigPhoto fileId={photoIds[idx]} />
                  {photoIds.length > 1 && (
                    <>
                      <button
                        data-testid="weekly-modal-carousel-prev"
                        onClick={() => setIdx((i) => (i - 1 + photoIds.length) % photoIds.length)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 text-ivory text-xl"
                        style={{ background: withAlpha(BRAND_COLORS.navy, 0.85) }}
                        aria-label="Previous"
                      >‹</button>
                      <button
                        data-testid="weekly-modal-carousel-next"
                        onClick={() => setIdx((i) => (i + 1) % photoIds.length)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 text-ivory text-xl"
                        style={{ background: withAlpha(BRAND_COLORS.navy, 0.85) }}
                        aria-label="Next"
                      >›</button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-ivory px-2 py-0.5 text-[10px] tracking-widest uppercase"
                        style={{ background: BRAND_COLORS.navy }}>
                        {idx + 1} <span className="mx-1 opacity-60">·</span> {photoIds.length}
                      </div>
                    </>
                  )}
                </div>
                {photoIds.length > 1 && (
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    {photoIds.map((pid, i) => (
                      <button
                        key={pid}
                        onClick={() => setIdx(i)}
                        className="w-2 h-2"
                        style={{
                          background: i === idx ? BRAND_COLORS.gold : withAlpha(BRAND_COLORS.navy, 0.25),
                        }}
                        aria-label={`Photo ${i + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div>
              <div className="label-caps mb-2">{de ? "Zusammenfassung" : "Summary"}</div>
              <p
                data-testid="weekly-modal-summary"
                className="text-[14px] text-navy-dark/85 leading-relaxed whitespace-pre-wrap"
              >
                {update?.summary || "—"}
              </p>
            </div>

            {update?.team_note && (
              <div>
                <div className="label-caps mb-2" style={{ color: BRAND_COLORS.gold }}>
                  {de ? "Persönliches Fazit" : "Personal note"}
                </div>
                <p className="text-[13px] text-navy-dark/75 italic leading-relaxed whitespace-pre-wrap">
                  {update.team_note}
                </p>
              </div>
            )}

            {activities.length > 0 && (
              <div>
                <div className="label-caps mb-2">{de ? "Erledigte Aufgaben" : "Completed tasks"}</div>
                <ul className="space-y-1.5 text-[13px] text-navy-dark/80" data-testid="weekly-modal-activities">
                  {activities.map((a, i) => (
                    <li key={a?.id ?? i} className="flex items-start gap-2">
                      <span className="mt-1 w-1 h-1 rounded-full shrink-0" style={{ background: BRAND_COLORS.gold }} />
                      <span className="min-w-0">{a?.title || "—"}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-auto pt-4 border-t border-navy/10 flex items-center justify-between gap-3 flex-wrap">
              <div className="label-caps">Weissblom Team</div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/weekly-updates/${update?.id}/pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="weekly-modal-pdf-btn"
                className="inline-flex items-center gap-1.5 border border-navy/25 hover:border-gold text-navy px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase transition-colors"
              >
                <FileDown className="w-3.5 h-3.5" strokeWidth={1.6} /> {de ? "Als PDF" : "As PDF"}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
